import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';
import { ValidationApiError } from '@/shared/api/ValidationApiError';

type AddOrganizationRequest = components['schemas']['AddOrganizationEndpoint_AddOrganizationRequest'];
type AddOrganizationResponse = components['schemas']['AddOrganizationEndpoint_AddOrganizationResponse'];

export function useAddOrganization() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (request: AddOrganizationRequest): Promise<AddOrganizationResponse> => {
      const client = createApiClient(getAccessToken);
      const { data } = await client.POST('/api/organizations', {
        body: request,
      });

      if (!data) {
        throw new Error('No data returned from API');
      }

      return data as AddOrganizationResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });

  return {
    addOrganization: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    validationErrors: mutation.error instanceof ValidationApiError ? mutation.error.errors : null,
  };
}
